import React, { createContext, ReactNode, useContext, useEffect, useState } from "react";
import { COMMON_RESOURCE } from "_common/resources/BaseResourceStore";
import { toast } from "react-toastify";
import i18next from "i18next";

type CompareContextType = {
    itemsSelected: CompareProps[];
    isModalOpen: boolean;
    openModal: () => void;
    closeModal: () => void;
    isAlreadyCompared: (itemId: string) => boolean;
    toggleCompare: (item: CompareProps) => void;
};

const CompareContext = createContext<CompareContextType | undefined>(undefined);

type CompareProviderProps = {
    children: ReactNode;
};

export interface CompareProps {
    itemId: string;
    resource: COMMON_RESOURCE;
    type?: "condo" | "house" | "villa" | "land" | "commercial" | "hotel";
}

export const CompareProvider: React.FC<CompareProviderProps> = ({ children }: CompareProviderProps) => {
    const [itemsSelected, setItemsSelected] = useState<CompareProps[]>([]);
    const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
    const hasAnyLand = itemsSelected.some((item) => item.type === "land");

    useEffect(() => {
        const lS = localStorage.getItem("itemsSelected");
        if (lS) {
            setItemsSelected(JSON.parse(lS));
        }
    }, []);

    const isAlreadyCompared = (itemId: string) => {
        return itemsSelected.some((i) => i.itemId === itemId);
    };

    const addItemToCompare = (payload: CompareProps) => {
        if (itemsSelected.length >= 5) return;
        if (
            itemsSelected.length > 0 &&
            ((hasAnyLand && payload.type !== "land") || (!hasAnyLand && payload.type === "land"))
        ) {
            toast.error(i18next.t("comparePage.landError"));
            return;
        }
        const lS = localStorage.getItem("itemsSelected");
        if (lS) {
            localStorage.setItem("itemsSelected", JSON.stringify([...JSON.parse(lS), payload]));
        } else {
            localStorage.setItem("itemsSelected", JSON.stringify([payload]));
        }
        setItemsSelected((prevSelected) => [...prevSelected, payload]);
    };

    const removeItemToCompare = (payload: CompareProps) => {
        const lS = localStorage.getItem("itemsSelected");
        if (lS) {
            localStorage.setItem(
                "itemsSelected",
                JSON.stringify(JSON.parse(lS).filter((item: CompareProps) => item.itemId !== payload.itemId)),
            );
        }
        setItemsSelected((prevSelected) => prevSelected.filter((item) => item.itemId !== payload.itemId));
    };

    const toggleCompare = (payload: CompareProps) => {
        if (isAlreadyCompared(payload.itemId)) {
            removeItemToCompare(payload);
        } else {
            addItemToCompare(payload);
        }
    };

    const openModal = () => {
        setIsModalOpen(true);
    };

    const closeModal = () => {
        setIsModalOpen(false);
    };

    return (
        <CompareContext.Provider
            value={{
                itemsSelected,
                isModalOpen,
                openModal,
                closeModal,
                isAlreadyCompared,
                toggleCompare,
            }}
        >
            {children}
        </CompareContext.Provider>
    );
};

export const useCompare = () => {
    const context = useContext(CompareContext);
    if (!context) {
        throw new Error("useCompare must be used within a CompareProvider");
    }
    return context;
};
